import React, { useEffect, useState } from "react";
import { getPayrollRequests } from "../../services";
import moment from "moment";
import _ from "lodash";
import { Bell, RefreshCw } from "lucide-react";
import { requestTypes, status, rembursment } from "../../utils/Helpers";
import { useNavigate } from "react-router-dom";

const AdminRequestNotifications = () => {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const fetchAdminRequests = async () => {
    setIsLoading(true);
    try {
      const fromDate = moment().subtract(15, "days").format("YYYY-MM-DD");
      const toDate = moment().add(1, "days").format("YYYY-MM-DD");

      const response = await getPayrollRequests("", fromDate, toDate, "", "", "0", "100");
      const result = response?.data?.Result;
      const allReqs = result?.RequestList || (Array.isArray(result) ? result : []);

      const windowStart = moment().subtract(15, "days").startOf("day");
      const windowEnd = moment().endOf("day");

      const filtered = allReqs.filter((req) => {
        const start = moment(req.StartDate);
        const end = req.EndDate ? moment(req.EndDate) : start;
        const overlapsWindow = start.isSameOrBefore(windowEnd) && end.isSameOrAfter(windowStart);

        const createdAt = moment(req.CreatedAt);
        const createdInWindow =
          createdAt.isSameOrAfter(windowStart) && createdAt.isSameOrBefore(windowEnd);

        return overlapsWindow || createdInWindow;
      });

      setRequests(_.orderBy(filtered, ["CreatedAt"], ["desc"]));
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminRequests();
    const interval = setInterval(fetchAdminRequests, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleAction = () => {
    navigate("/allrequests");
  };

  const getStatusStyles = (requestStatus) => {
    const base = "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border";
    switch (requestStatus) {
      case 1:
        return `${base} bg-white/10 border-gray-400/20 text-gray-700 dark:bg-white/5 dark:border-gray-300/15 dark:text-gray-200`;
      case 2:
        return `${base} bg-red-500/15 border-red-400/25 text-red-700 dark:bg-red-500/10 dark:text-red-200`;
      case 3:
        return `${base} bg-green-500/15 border-green-400/25 text-green-700 dark:bg-green-500/10 dark:text-green-200`;
      default:
        return `${base} bg-white/10 border-gray-400/20 text-gray-700 dark:bg-white/5 dark:border-gray-300/15 dark:text-gray-200`;
    }
  };

  const formatAlertDate = (req) => {
    if (req?.RequestType === "Reimbursement") {
      return req?.Date ? moment(req.Date).format("DD/MM/YY") : "";
    }

    const start = req?.StartDate ? moment(req.StartDate) : null;
    const end = req?.EndDate ? moment(req.EndDate) : null;
    if (!start || !start.isValid()) return "";
    if (!end || !end.isValid()) return start.format("DD/MM/YY");
    return `${start.format("DD/MM/YY")} / ${end.format("DD/MM/YY")}`;
  };

  const formatCreatedAt = (date) => (date ? moment(date).format("DD/MM/YYYY, h:mm A") : "");

  return (
    <div className="mt-1 xl:w-[40%] lg:w-[60%] md:w-full w-full relative group/dashboard-live-status">
      <div
        className="absolute -inset-[1px] bg-gradient-to-r from-blue-600/40 via-indigo-600/40 to-blue-600/40 rounded-2xl blur-sm opacity-50 group-hover/dashboard-live-status:opacity-80 transition duration-500 pointer-events-none"
        aria-hidden="true"
      />

      <div className="relative bg-white/70 dark:bg-black/40 backdrop-blur-3xl rounded-2xl border-2 border-blue-500/20 dark:border-blue-400/20 shadow-[0_15px_35px_-10px_rgba(0,0,0,0.4)] h-[467px] flex flex-col">
        <div className="px-4 py-3 border-b border-blue-500/10 flex-shrink-0 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <span
                className="inline-flex w-9 h-9 shrink-0 rounded-xl bg-gray-100 dark:bg-white/[0.06] border border-gray-200/90 dark:border-white/10 items-center justify-center"
                aria-hidden="true"
              >
                <Bell className="w-4 h-4 text-gray-600 dark:text-gray-400" strokeWidth={2} />
              </span>
              Live Status
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {requests.length} Alerts Detected (last 15 days)
            </p>
          </div>

          <button
            onClick={fetchAdminRequests}
            className="p-2 rounded-xl hover:bg-blue-600 hover:text-white transition-all border border-blue-500/20 dark:border-blue-400/20 flex items-center justify-center"
            title="Refresh"
          >
            <RefreshCw
              className={`w-4 h-4 ${isLoading ? "animate-spin" : "text-gray-400"} dark:text-gray-300`}
            />
          </button>
        </div>

        <div
          className="flex-1 overflow-y-auto px-4 py-3
           [&::-webkit-scrollbar]:w-1.5
          [&::-webkit-scrollbar-track]:rounded-full
          [&::-webkit-scrollbar-track]:bg-gray-100
          [&::-webkit-scrollbar-thumb]:rounded-full
          [&::-webkit-scrollbar-thumb]:bg-gray-300
          dark:[&::-webkit-scrollbar-track]:bg-gray-900
          dark:[&::-webkit-scrollbar-thumb]:bg-blue-gray-900"
        >
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
            </div>
          ) : !_.isEmpty(requests) ? (
            <table className="w-full">
              <thead>
                <tr className="bg-white/5 text-left">
                                    <th className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-300 text-center">MEMBER</th>

                  <th className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-300">DATE</th>
                  <th className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-300">TYPE</th>
                  <th className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-300">STATUS</th>
                  <th className="px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-300">CREATED</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-white/10">
                {requests.map((req, idx) => (
                  <tr
                    key={req?.Id || idx}
                    className="hover:bg-white/[0.06] dark:hover:bg-white/[0.08] transition-colors cursor-pointer"
                    onClick={handleAction}
                  >
                     <td className="px-3 py-2.5 text-center">
                      {req?.Photopath ? (
                        <img
                          src={req.Photopath}
                          alt=""
                          className="w-8 h-8 rounded-2xl object-cover border border-blue-500/20 bg-white/5"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full border border-blue-500/20 bg-white/5" />
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-900 dark:text-gray-200">
                      {formatAlertDate(req)}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-900 dark:text-gray-200">
                      {req?.RequestType === "Reimbursement"
                        ? rembursment[req?.RequestSubType] || "Reimbursement"
                        : requestTypes[req?.RequestType] || "Leave"}
                    </td>
                   
                    <td className="px-3 py-2.5">
                      <span className={getStatusStyles(req?.RequestStatus)}>
                        <span
                          className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                            req?.RequestStatus === 1
                              ? "bg-gray-400"
                              : req?.RequestStatus === 2
                              ? "bg-red-400"
                              : req?.RequestStatus === 3
                              ? "bg-green-400"
                              : "bg-gray-400"
                          }`}
                        />
                        {status[req?.RequestStatus]}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-sm text-gray-900 dark:text-gray-200">
                      {formatCreatedAt(req?.CreatedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-10">
              <div className="text-gray-500 dark:text-gray-400">
                <svg className="w-6 h-6 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-sm font-medium">No alerts available</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Live alerts will appear here</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminRequestNotifications;
